import { Modal, Button, Icon, Text } from "@artprov/design-system";
import PropTypes from "prop-types";

/**
 * Modal genérico para confirmación de acciones masivas
 * Reutilizable para Descartar, Reprocesar y otras acciones futuras
 *
 * @param {Object} props
 * @param {boolean} props.open - Estado de apertura del modal
 * @param {Function} props.setOpen - Función para cambiar el estado de apertura
 * @param {Array} props.selectedRows - Filas seleccionadas para la acción
 * @param {string} props.icon - Nombre del icono a mostrar
 * @param {string} props.iconColor - Clase de color del icono (ej: "text-warning-20")
 * @param {string} props.iconSize - Tamaño del icono (ej: "extra-large")
 * @param {string} props.title - Título del modal
 * @param {string} props.message - Mensaje descriptivo
 * @param {string} props.cancelText - Texto del botón de cancelar
 * @param {string} props.confirmText - Texto del botón de confirmar
 * @param {Function} props.onConfirm - Callback al confirmar (recibe selectedRows)
 * @param {string} props.width - Ancho del modal (default: "408px")
 * @param {string} props.height - Alto del modal (default: "auto")
 */
export default function ModalAccionMasiva({
    open,
    setOpen,
    selectedRows,
    icon = "error",
    iconColor = "text-warning-20",
    iconSize = "extra-large",
    title,
    message,
    cancelText = "Cancelar",
    confirmText = "Confirmar",
    onConfirm,
    width = "408px",
    height = "auto",
}) {
    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirm = () => {
        onConfirm?.(selectedRows);
    };

    return (
        <Modal.Container
            open={open}
            setOpen={setOpen}
            closeOnClickOutside={false}
            onClose={handleClose}
        >
            <Modal.Content height={height} width={width}>
                <Modal.Header className="relative flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <Icon
                            icon={icon}
                            className={`${iconColor} mb-5`}
                            size={iconSize}
                        />
                        <Modal.Title className="pl-0 text-center text-[24px] tracking-[0.25px]">
                            {title}
                        </Modal.Title>
                    </div>
                    <div className="absolute right-0 top-0">
                        <Modal.Close onClose={handleClose} />
                    </div>
                </Modal.Header>

                <Text variant="Body-M" className="text-center tracking-[0.1px]">
                    {message}
                </Text>

                <Modal.Footer className="flex justify-center">
                    <div className="flex gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClose}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleConfirm}
                        >
                            {confirmText}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal.Content>
        </Modal.Container>
    );
}

ModalAccionMasiva.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    selectedRows: PropTypes.array.isRequired,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    iconSize: PropTypes.string,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    cancelText: PropTypes.string,
    confirmText: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    width: PropTypes.string,
    height: PropTypes.string,
};
